import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PlanTier } from '@tilly/shared';

export interface BusinessMembership {
  id: string;
  businessId: string;
  businessName?: string;
  role: 'owner' | 'admin' | 'provider' | 'customer';
  providerType?: 'employee' | 'contractor';
  status: 'pending' | 'approved' | 'rejected' | 'active' | 'suspended';
  plan?: PlanTier;
}

interface BusinessState {
  // Memberships for the current user across all businesses
  memberships: BusinessMembership[];
  activeBusinessId: string | null;
  activeBusinessName: string | null;

  // Derived state (computed from active membership)
  activeRole: ('owner' | 'admin' | 'provider' | 'customer') | null;
  activeStatus: ('pending' | 'approved' | 'rejected' | 'active' | 'suspended') | null;
  activePlan: PlanTier | null;

  // Actions
  setMemberships: (memberships: BusinessMembership[]) => void;
  setActiveBusinessId: (businessId: string) => void;
  setActiveBusinessName: (name: string) => void;
  clearMemberships: () => void;

  // Selectors
  getActiveMembership: () => BusinessMembership | null;
}

export const useBusinessStore = create<BusinessState>()(
  persist(
    (set, get) => ({
      memberships: [],
      activeBusinessId: null,
      activeBusinessName: null,
      activeRole: null,
      activeStatus: null,
      activePlan: null,

      setMemberships: (memberships) => {
        set({ memberships });
        const state = get();
        if (memberships.length > 0) {
          // Update activeBusinessId if it's currently null OR stale (not in the new memberships list)
          const isCurrentValid =
            state.activeBusinessId != null &&
            memberships.some((m) => m.businessId === state.activeBusinessId);
          if (!isCurrentValid) {
            set({
              activeBusinessId: memberships[0].businessId,
              activeBusinessName: memberships[0].businessName ?? null,
              activeRole: memberships[0].role,
              activeStatus: memberships[0].status,
              activePlan: memberships[0].plan || 'team',
            });
          } else {
            // Keep derived name/role/status/plan fresh when server-side membership
            // changes but the active business ID remains the same (e.g. pending ->
            // approved). This also guards against a stale `activeBusinessName`
            // left over in persisted storage from a previous user/session.
            const activeMembership = memberships.find(
              (m) => m.businessId === state.activeBusinessId
            );
            if (activeMembership) {
              set({
                activeBusinessName: activeMembership.businessName ?? state.activeBusinessName,
                activeRole: activeMembership.role,
                activeStatus: activeMembership.status,
                activePlan: activeMembership.plan || 'team',
              });
            }
          }
        } else {
          // No memberships → clear stale active business state
          set({
            activeBusinessId: null,
            activeRole: null,
            activeStatus: null,
            activePlan: null,
          });
        }
      },

      setActiveBusinessId: (businessId) => {
        const state = get();
        const membership = state.memberships.find((m) => m.businessId === businessId);
        if (membership) {
          set({
            activeBusinessId: businessId,
            activeBusinessName: membership.businessName ?? null,
            activeRole: membership.role,
            activeStatus: membership.status,
            activePlan: membership.plan || 'team',
          });
        }
      },

      setActiveBusinessName: (name) => set({ activeBusinessName: name }),

      clearMemberships: () =>
        set({
          memberships: [],
          activeBusinessId: null,
          activeBusinessName: null,
          activeRole: null,
          activeStatus: null,
          activePlan: null,
        }),

      getActiveMembership: () => {
        const state = get();
        return state.memberships.find((m) => m.businessId === state.activeBusinessId) || null;
      },
    }),
    {
      name: 'tilly-business-storage',
    }
  )
);
